import PropTypes from "prop-types";
import mainStyle from "./mainStyle.module.scss";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useZustandStore from "zustandStore";

// material-ui
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project import
import MainCard from "components/MainCard";
import CustomMenu from "components/Menu";
import navigation from "menu-items";
import UnSavedConfirmModalContainer from "components/UnSavedConfirmModalContainer";

// assets
import { ReactComponent as MspIcon } from "assets/images/icons/icn_table_cell_tag_msp.svg";
import { ReactComponent as OrgIcon } from "assets/images/icons/icn_table_cell_tag_org.svg";
import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up_light.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down_light.svg";
import { ReactComponent as DrawerExpand } from "assets/images/icons/icn_drawer_expand.svg";
import { ReactComponent as DrawerExpandHover } from "assets/images/icons/icn_drawer_expand_hover.svg";
import { ReactComponent as DrawerCollapse } from "assets/images/icons/icn_drawer_collapse.svg";
import { ReactComponent as DrawerCollapseHover } from "assets/images/icons/icn_drawer_collapse_hover.svg";
import { ReactComponent as Search } from "assets/images/icons/icn_search.svg";
import { ReactComponent as SearchHover } from "assets/images/icons/icn_search_hover.svg";
import { ReactComponent as ViewAll } from "assets/images/icons/icn_table_cell_ViewAll.svg";
import { ReactComponent as ViewAllHover } from "assets/images/icons/icn_table_cell_ViewAll_hover.svg";

import localStorageUtil from "utils/localStorageUtil";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;

const iconMap = {
  msp: MspIcon,
  org: OrgIcon,
};

const StyledSelect = ({
  value,
  onChange,
  options,
  iconType,
  isDataChanged,
  isShowUnsavedModal,
  setIsShowUnsavedModal,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchHover, setSearchHover] = useState(false);
  const [viewAllHover, setViewAllHover] = useState(false);
  const selectorRef = useRef(null);
  const menuOpen = Boolean(anchorEl);
  const alignMenuToSelectorLeft = ["msp", "org", "site"].includes(iconType);

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(search.toLowerCase()),
  );

  const currentValue = options.find((item) => item.id === value);
  const DynamicIcon =
    iconMap[
      currentValue?.hasManageOrg === false && iconType === "msp"
        ? "org"
        : iconType
    ];

  const handleOpen = (event) => {
    if (isDataChanged) {
      // Prevent the dropdown from opening when there is unsaved data ( e.g. SSID, Port Management ).
      setIsShowUnsavedModal(true);
      return;
    }

    setAnchorEl(
      alignMenuToSelectorLeft ? selectorRef.current : event.currentTarget,
    );
  };

  const handleClose = () => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    setAnchorEl(null);
    setSearch("");
  };

  return (
    <Box
      ref={selectorRef}
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #707ca8",
        borderRadius: "8px",
        mr: 2,
        width: 158,
        height: 32,
        bgcolor: "#fff",
        position: "relative",
        px: 1,
        cursor: "pointer",
      }}
    >
      <Box
        onClick={() => onChange(currentValue, iconType)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          overflow: "hidden",
          flexGrow: 1,
          zIndex: 1,
        }}
      >
        {DynamicIcon && <DynamicIcon />}
        <EllipsisTooltipCell
          text={currentValue?.name}
          placement="bottom"
          sx={{
            width: "95px",
            fontSize: theme.typography.customFontSize[2],
            color: "primary.main",
            fontWeight: theme.typography.customFontWeight[3],
          }}
        />
      </Box>

      <Box
        onClick={
          checkEnvironment !== "premises" || iconType !== "msp"
            ? handleOpen
            : null
        }
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 32,
          height: "100%",
          bgcolor: "#172264",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
          pointerEvents: "auto",
          zIndex: 2,
        }}
      >
        {menuOpen ? <ChevronUp /> : <ChevronDown />}
      </Box>
      <CustomMenu
        anchorEl={anchorEl}
        open={menuOpen}
        handleMenuClose={handleClose}
        mt={alignMenuToSelectorLeft ? 1 : 0}
        maxWidth={alignMenuToSelectorLeft ? 275 : 158}
        minWidth={alignMenuToSelectorLeft ? 275 : undefined}
        anchorHorizontal={alignMenuToSelectorLeft ? "left" : "right"}
        transformHorizontal={alignMenuToSelectorLeft ? "left" : "right"}
      >
        <Box
          onMouseEnter={() => setSearchHover(true)}
          onMouseLeave={() => setSearchHover(false)}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "#fff",
            px: "10px",
            pt: "10px",
            pb: "5px",
          }}
        >
          <TextField
            placeholder='Search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: alignMenuToSelectorLeft ? "255px" : "138px",
              height: "24px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#ffffff",
                "& fieldset": {
                  borderColor: "#dddddd",
                },
                "&:hover fieldset": {
                  borderColor: "primary.mainHover",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "7px 14px",
                  fontSize: theme.typography.customFontSize[2],
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {searchHover ? <SearchHover /> : <Search />}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List
          className={mainStyle["custom-scrollbar"]}
          sx={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            paddingTop: "5px",
            paddingLeft: "10px",
            maxHeight: "180px",
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "content-box",
            marginRight: "10px",
          }}
        >
          {filteredOptions.map((opt) => {
            const DynamicOptionIcon =
              iconMap[
                opt.hasManageOrg === false && iconType === "msp"
                  ? "org"
                  : iconType
              ];
            const isSelected = value === opt.id || value === opt;
            return (
              <ListItem
                key={opt.id}
                button
                selected={isSelected}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(opt, iconType);
                  handleClose();
                }}
                sx={{
                  color: "primary.main",
                  paddingLeft: "10px",
                  margin: "5px 5px 0 0",
                  borderRadius: "5px",
                  width: alignMenuToSelectorLeft ? "255px" : "140px",
                  height: "40px",
                  "&:hover": { backgroundColor: "#e8ebfc" },
                  "&.Mui-selected": { backgroundColor: "#dbe0fc" },
                }}
              >
                {DynamicOptionIcon && (
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <DynamicOptionIcon style={{ marginRight: 8 }} />
                  </ListItemIcon>
                )}
                <EllipsisTooltipCell
                  text={opt.name}
                  placement="right"
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    fontSize: theme.typography.customFontSize[2],
                  }}
                />
              </ListItem>
            );
          })}
        </List>
        {iconType === "msp" && (
          <List>
            <Divider component='li' variant='middle' />
            <ListItem
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
                cursor: "pointer",
                color: "primary.main",
                margin: "10px 10px 5px",
                left: "8px",
                borderRadius: "5px",
                width: alignMenuToSelectorLeft ? "255px" : "128px",
                height: "20px",
                "&:hover .MuiListItemText-primary": {
                  color: "primary.mainHover",
                },
              }}
              onMouseEnter={() => setViewAllHover(true)}
              onMouseLeave={() => setViewAllHover(false)}
              onClick={(e) => {
                e.stopPropagation();
                navigate("/realms");
                handleClose();
              }}
            >
              <ListItemIcon sx={{ minWidth: 24, fontSize: 20 }}>
                {viewAllHover ? <ViewAllHover /> : <ViewAll />}
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  style: {
                    fontSize: theme.typography.customFontSize[2],
                    whiteSpace: "nowrap",
                  },
                }}
                primary='View All'
              />
            </ListItem>
          </List>
        )}
      </CustomMenu>
    </Box>
  );
};

export default function Breadcrumbs({
  scopeData,
  // setScopeData,
  card = false,
  custom = false,
  divider = false,
  heading,
  icon,
  icons,
  links,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [main, setMain] = useState();
  const [item, setItem] = useState();
  const [selectedRealm, setSelectedRealm] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showSelector, setShowSelector] = useState(true);
  const [isCollapseHovered, setIsCollapseHovered] = useState(false);

  // Unsaved blocker.
  const isDataChanged = useZustandStore((state) => state.isDataChanged);
  const onGlobalApplyFn = useZustandStore((state) => state.onGlobalApplyFn);
  const onGlobalDiscardFn = useZustandStore((state) => state.onGlobalDiscardFn);
  const setIsNavDisabled = useZustandStore((state) => state.setIsNavDisabled);
  const setServiceStatus = useZustandStore((state) => state.setServiceStatus);
  const [isShowUnsavedModal, setIsShowUnsavedModal] = useState(false);

  const getSupportSession = () => {
    try {
      const rawSession = sessionStorage.getItem("supportSession");
      return rawSession ? JSON.parse(rawSession) : null;
    } catch (err) {
      return null;
    }
  };

  const supportSession = getSupportSession();

  let storedOriginalRealmData = scopeData?.realm || [];
  let storedOriginalOrgData = scopeData?.orgs || [];
  let storedOriginalSiteData = scopeData?.site || [];

  let storedSelectedRealmData = localStorageUtil.getItemField(
    "selectBreadcrumbs",
    "realm",
  );
  let storedSelectedOrgData = localStorageUtil.getItemField(
    "selectBreadcrumbs",
    "orgs",
  );
  let storedSelectedSiteData = localStorageUtil.getItemField(
    "selectBreadcrumbs",
    "site",
  );

  if (supportSession && supportSession.orgName) {
    const targetOrgId =
      supportSession.orgId || storedSelectedOrgData?.id || "support-org";
    const targetOrgName = supportSession.orgName;

    if (storedOriginalOrgData.length > 0) {
      let found = false;
      storedOriginalOrgData = storedOriginalOrgData.map((org) => {
        if (!supportSession.orgId || org.id === targetOrgId) {
          found = true;
          return { ...org, name: targetOrgName, id: targetOrgId };
        }
        return org;
      });

      if (!found) {
        storedOriginalOrgData = [
          { id: targetOrgId, name: targetOrgName },
          ...storedOriginalOrgData,
        ];
      }
    } else {
      storedOriginalOrgData = [{ id: targetOrgId, name: targetOrgName }];
    }

    if (
      !storedSelectedOrgData ||
      Object.keys(storedSelectedOrgData).length === 0
    ) {
      storedSelectedOrgData = { id: targetOrgId, name: targetOrgName };
    } else {
      storedSelectedOrgData = {
        ...storedSelectedOrgData,
        name: targetOrgName,
        id: targetOrgId,
      };
    }
  }

  const { setBread } = useBreadStorageContext();

  useEffect(() => {
    if (scopeData) {
      storedOriginalRealmData = scopeData.realm;
      storedOriginalOrgData = scopeData.orgs;
      storedOriginalSiteData = scopeData.site;

      let nowSelectedBread = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );
      if (Object.keys(nowSelectedBread).length !== 0) {
        if (
          nowSelectedBread.realm &&
          Object.keys(nowSelectedBread.realm).length === 0
        ) {
          setSelectedRealm(null);
        }
        if (
          nowSelectedBread.orgs &&
          Object.keys(nowSelectedBread.orgs).length === 0
        ) {
          setSelectedOrg(null);
        }
        if (
          nowSelectedBread.site &&
          Object.keys(nowSelectedBread.site).length === 0
        ) {
          setSelectedSite(null);
        }
      }

      let customLocation = location.pathname;
      if (customLocation.includes("/components-overview/breadcrumbs")) {
        customLocation = "/apps/customer/customer-card";
      }

      navigation?.items?.forEach((menu) => {
        if (menu.type === "group") {
          if (menu.url === customLocation) {
            setMain(menu);
            setItem(menu);
          } else {
            getCollapse(menu);
          }
        }
      });
    }
  }, [scopeData]);

  useEffect(() => {
    if (
      storedSelectedRealmData &&
      Object.keys(storedSelectedRealmData).length > 0 &&
      storedSelectedRealmData.id !== selectedRealm
    ) {
      setSelectedRealm(storedSelectedRealmData.id);
    }

    const currentOrgId = supportSession?.orgId || storedSelectedOrgData?.id;
    if (
      storedSelectedOrgData &&
      Object.keys(storedSelectedOrgData).length > 0 &&
      currentOrgId !== selectedOrg
    ) {
      setSelectedOrg(currentOrgId);
    }

    if (
      storedSelectedSiteData &&
      Object.keys(storedSelectedSiteData).length > 0 &&
      storedSelectedSiteData.id !== selectedSite
    ) {
      setSelectedSite(storedSelectedSiteData.id);
    } else if (
      !storedSelectedSiteData ||
      Object.keys(storedSelectedSiteData).length === 0
    ) {
      setSelectedSite(null);
    }
  }, [storedSelectedRealmData, storedSelectedOrgData, storedSelectedSiteData]);

  const getCollapse = (menu) => {
    if (!custom && menu.children) {
      menu.children.forEach((collapse) => {
        if (collapse.type === "collapse") {
          getCollapse(collapse);
          if (collapse.url === location.pathname) {
            setMain(collapse);
            setItem(collapse);
          }
        } else if (collapse.type === "item") {
          if (location.pathname === collapse.url) {
            setMain(menu);
            setItem(collapse);
          }
        }
      });
    }
  };

  const breadcrumbItems = [];

  if (main?.type === "collapse" && main.breadcrumbs !== false) {
    breadcrumbItems.push(
      <Typography
        key='main'
        variant='h6'
        sx={{
          color: "primary.greyPrimary",
          fontSize: theme.typography.customFontSize[3],
        }}
      >
        {main.title}
      </Typography>,
    );
  }

  if (main && item?.type === "item") {
    if (main?.type === "collapse" && main.breadcrumbs !== false) {
      breadcrumbItems.push(
        <span key='separator' style={{ display: "flex", alignItems: "center" }}>
          &gt;
        </span>,
      );
    }

    breadcrumbItems.push(
      <Typography
        key='item'
        variant='subtitle1'
        sx={{
          color: "primary.greyPrimary",
          fontWeight: "bold",
          fontSize: "15px",
        }}
      >
        {item.title}
      </Typography>,
    );
  }

  function handleSelectChange(newValue, iconType) {
    const currentType =
      iconType === "msp" ? "realm" : iconType === "org" ? "orgs" : "site";

    if (currentType === "site") {
      setServiceStatus(null);
      setIsNavDisabled(true);
    }

    localStorageUtil.updateItemField(
      "selectBreadcrumbs",
      currentType,
      newValue,
    );
    if (currentType === "realm") {
      if (
        storedSelectedRealmData?.id === newValue?.id &&
        location.pathname === "/realms"
      )
        return;
      setSelectedRealm(newValue?.id);
      localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
      localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
      setSelectedOrg(null);
      setSelectedSite(null);
      newValue?.hasManageOrg
        ? navigate("/msp/organizations")
        : navigate("/org/sites");
    }
    if (currentType === "orgs") {
      if (
        storedSelectedOrgData?.id === newValue?.id &&
        location.pathname === "/msp/organizations"
      )
        return;
      setSelectedOrg(newValue?.id);
      localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
      setSelectedSite(null);
      navigate("/org/sites");
    }
    if (currentType === "site") {
      setSelectedSite(newValue?.id);
      const selectedSystem =
        localStorage.getItem("selectedSystem") || "network";
      navigate(
        selectedSystem === "surveillance"
          ? "/surveillance/dashboard"
          : "/site/dashboard",
      );
    }

    let nowSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );
    setBread(nowSelectedBread);
  }

  const isSiteValid =
    storedOriginalSiteData.length > 0 &&
    selectedSite &&
    storedSelectedSiteData &&
    Object.keys(storedSelectedSiteData).length > 0;

  return (
    <MainCard
      border={card}
      sx={
        card
          ? { mb: 2, ...sx }
          : { mb: 2, bgcolor: "inherit", backgroundImage: "none", ...sx }
      }
      {...others}
      content={card}
      shadow='none'
    >
      <Grid container alignItems='center' spacing={1}>
        <Grid item xs>
          <Box display='flex' alignItems='center'>
            <Box display='flex' alignItems='center'>
              <Box display='flex' alignItems='center'>
                {showSelector ? (
                  <>
                    {!supportSession && (
                      <StyledSelect
                        value={selectedRealm || storedSelectedRealmData?.id}
                        onChange={handleSelectChange}
                        options={storedOriginalRealmData}
                        iconType='msp'
                        isDataChanged={isDataChanged}
                        isShowUnsavedModal={isShowUnsavedModal}
                        setIsShowUnsavedModal={setIsShowUnsavedModal}
                      />
                    )}

                    {storedOriginalOrgData.length > 0 && selectedOrg && (
                      <StyledSelect
                        value={selectedOrg || storedSelectedOrgData?.id}
                        onChange={handleSelectChange}
                        options={storedOriginalOrgData}
                        iconType='org'
                        isDataChanged={isDataChanged}
                        isShowUnsavedModal={isShowUnsavedModal}
                        setIsShowUnsavedModal={setIsShowUnsavedModal}
                      />
                    )}

                    {isSiteValid && (
                      <StyledSelect
                        value={selectedSite || storedSelectedSiteData?.id}
                        onChange={handleSelectChange}
                        options={storedOriginalSiteData}
                        iconType='site'
                        isDataChanged={isDataChanged}
                        isShowUnsavedModal={isShowUnsavedModal}
                        setIsShowUnsavedModal={setIsShowUnsavedModal}
                      />
                    )}
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mr: 2,
                      fontSize: 15,
                      fontWeight: theme.typography.customFontWeight[1],
                      color: "primary.main",
                    }}
                  >
                    {!supportSession && (
                      <>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {storedSelectedRealmData?.hasManageOrg ? (
                            <MspIcon />
                          ) : (
                            <OrgIcon />
                          )}
                          {storedSelectedRealmData?.name}
                        </Box>
                        {(selectedOrg || isSiteValid) && <Box>/</Box>}
                      </>
                    )}

                    {storedOriginalOrgData.length > 0 && selectedOrg && (
                      <>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <OrgIcon />
                          {storedSelectedOrgData?.name}
                        </Box>
                        {isSiteValid && <Box>/</Box>}
                      </>
                    )}

                    {isSiteValid && <Box>{storedSelectedSiteData?.name}</Box>}
                  </Box>
                )}
              </Box>

              <IconButton
                style={{ backgroundColor: "transparent" }}
                onClick={() => setShowSelector((prev) => !prev)}
                onMouseEnter={() => setIsCollapseHovered(true)}
                onMouseLeave={() => setIsCollapseHovered(false)}
              >
                {showSelector ? (
                  isCollapseHovered ? (
                    <DrawerCollapseHover />
                  ) : (
                    <DrawerCollapse />
                  )
                ) : isCollapseHovered ? (
                  <DrawerExpandHover />
                ) : (
                  <DrawerExpand />
                )}
              </IconButton>
            </Box>
            <Box ml={1}>
              <MuiBreadcrumbs
                aria-label='breadcrumb'
                maxItems={maxItems || 8}
                separator=''
              >
                {breadcrumbItems}
              </MuiBreadcrumbs>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <UnSavedConfirmModalContainer
        isOpen={isShowUnsavedModal}
        applyCallback={() => {
          onGlobalApplyFn();
          setIsShowUnsavedModal(false);
        }}
        discardCallback={() => {
          onGlobalDiscardFn();
          setIsShowUnsavedModal(false);
        }}
      />
    </MainCard>
  );
}

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  custom: PropTypes.bool,
  divider: PropTypes.bool,
  heading: PropTypes.string,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  links: PropTypes.array,
  maxItems: PropTypes.number,
  rightAlign: PropTypes.bool,
  separator: PropTypes.any,
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  sx: PropTypes.any,
  others: PropTypes.any,
};
